import type {
  SimpleBinaryExpression,
  ExpressionItem,
  SimpleExpression,
  FieldMetadata,
  OperatorInfo,
} from './expression';

export {
  LogicalOperator,
  Operator,
  DataType,
  MetadataContextType,
  OperandType,
} from './expression';

export type {
  SimpleBinaryExpression,
  ExpressionItem,
  SimpleExpression,
  FieldMetadata,
  OperatorInfo,
};

export { type EnumValueData } from './enum';

// ===== Waterfall-Specific Enums =====

export enum IntegrationType {
  None = 'None',
  Credit = 'Credit',
  Underwrite = 'Underwrite',
  PropertyData = 'PropertyData',
  Los = 'Los',
  Pricing = 'Pricing',
  Email = 'Email',
  Flood = 'Flood',
  IncomeEmployment = 'IncomeEmployment',
  Asset = 'Asset',
  Fees = 'Fees',
}

export enum WaterfallRuleType {
  StepCondition = 'StepCondition',
  ExitCondition = 'ExitCondition',
  RequestSetting = 'RequestSetting',
}

export enum WaterfallStepAction {
  Continue = 'Continue',
  Abort = 'Abort',
}

export enum WaterfallExecutionMode {
  StepEvaluation = 'StepEvaluation',
  AI = 'AI',
}

export enum WaterfallExecutionStatus {
  Running = 'Running',
  Complete = 'Complete',
  Error = 'Error',
  Abort = 'Abort',
}

export enum MultipleStepExecutionMode {
  First = 'First',
  All = 'All',
}

// ===== Waterfall Rule Models =====

export interface OverrideSettingViewModel {
  fieldName: string | null;
  value: string | null;
}

export interface WaterfallRuleViewModel {
  expression?: SimpleExpression | null;
  ruleType: WaterfallRuleType;
  overrideSetting?: OverrideSettingViewModel | null;
}

// ===== Provider Selection Strategy =====

export enum ProviderSelectionStrategy {
  RoundRobin = 'RoundRobin',
}

// ===== Step Model =====

export interface StepViewModel {
  description?: string | null;
  stepGuid: string;
  nextSteps?: StepViewModel[] | null;
  /** List of integration settings IDs for this step. When multiple providers are configured, the selectionStrategy determines which provider to use for each execution. */
  integrationSettingsIds: number[];
  /** The selection strategy to use when multiple integration settings are configured. Required when integrationSettingsIds contains more than one entry. */
  selectionStrategy?: ProviderSelectionStrategy | null;
  waterfallRules?: WaterfallRuleViewModel[] | null;
  stepTimeoutInMinutes?: number | null;
  onTimeoutExceededAction?: WaterfallStepAction;
  onFatalErrorAction?: WaterfallStepAction;
  /** Waterfall ID to execute when a disconnect event occurs on this step. */
  onDisconnectWaterfallId?: number | null;
  /** Waterfall ID to execute when a cancel event occurs on this step. */
  onCancelWaterfallId?: number | null;
  /** Determines how the waterfall handles multiple child steps when evaluating next steps. When set to All, all matching child steps will be executed. When set to First (default), only the first matching child step is executed. */
  multipleStepExecutionMode?: MultipleStepExecutionMode;
}

// ===== Refresh Order Settings =====

export interface RefreshOrderSettingsViewModel {
  onCancelWaterfallId?: number | null;
  onDisconnectWaterfallId?: number | null;
  timeoutInMinutes?: number | null;
}

// ===== Waterfall Version Models =====

export interface WaterfallVersionViewModel {
  tenantId: string;
  createdDate: string;
  createdBy: string;
  updatedDate?: string | null;
  updatedBy?: string | null;
  waterfallVersionId: number;
  waterfallId: number;
  versionNumber: number;
  executionMode: WaterfallExecutionMode;
  initialSteps: StepViewModel[] | null;
  initialStepsExecutionMode: MultipleStepExecutionMode;
  refreshOrderSettings?: RefreshOrderSettingsViewModel | null;
  alwaysSendParentWaterfallDetailInWebhookEvents: boolean;
  notes?: string | null;
}

export interface CreateOrUpdateWaterfallVersionViewModel {
  waterfallVersionId?: number | null;
  waterfallId?: number;
  executionMode: WaterfallExecutionMode;
  initialSteps: StepViewModel[] | null;
  initialStepsExecutionMode: MultipleStepExecutionMode;
  refreshOrderSettings?: RefreshOrderSettingsViewModel | null;
  alwaysSendParentWaterfallDetailInWebhookEvents: boolean;
}

// ===== Waterfall Models =====

export interface WaterfallViewModel {
  tenantId: string;
  createdDate: string;
  createdBy: string;
  updatedDate?: string | null;
  updatedBy?: string | null;
  waterfallId: number;
  name: string;
  description: string;
  integrationType: IntegrationType;
  currentVersionId?: number | null;
  lastExecuted?: string | null;
  disabled: boolean;
  archived: boolean;
  executionOrder: number;
  currentVersion?: WaterfallVersionViewModel | null;
  versions?: WaterfallVersionViewModel[] | null;
}

export interface CreateOrUpdateWaterfallViewModel {
  waterfallId?: number | null;
  name: string;
  description: string;
  integrationType: IntegrationType;
  currentVersionId?: number | null;
  disabled?: boolean;
  archived?: boolean;
}

// ===== Trace/Execution Models =====

export interface WaterfallTraceRecordViewModel {
  tenantId: string;
  createdDate: string;
  createdBy: string;
  updatedDate?: string | null;
  updatedBy?: string | null;
  waterfallExecutionRecordId: number;
  waterfallId: number;
  waterfallVersionId: number;
  dateStarted: string;
  status: WaterfallExecutionStatus;
  waterfallVersion: WaterfallVersionViewModel;
  isTestMode: boolean;
  testOverrides: string | null;
  borrowerIdentifier: string | null;
}

export interface WaterfallExecutionRecordViewModel {
  tenantId: string;
  createdDate: string;
  createdBy: string;
  updatedDate?: string | null;
  updatedBy?: string | null;
  waterfallExecutionRecordId: number;
  waterfallExecutionRecordGuid: string;
  waterfallId: number;
  waterfallVersionId: number;
  status: WaterfallExecutionStatus;
  loanNumber: string | null;
  leadNumber: string | null;
  loanId: string | null;
  requestData: string | null;
  dateStarted: string | null;
  dateCompleted: string | null;
  inactive: boolean;
  isTestMode: boolean;
  testOverrides: string | null;
  parentWaterfallExecutionRecordId: number | null;
  waterfall?: WaterfallViewModel | null;
  waterfallVersion?: WaterfallVersionViewModel | null;
}

// ===== Form/UI Models =====

export interface WaterfallFormModel {
  name: string;
  description: string;
  integrationType: IntegrationType;
  selectedVersion?: number | null;
}

// ===== Migration Models =====

export enum WaterfallDiffAction {
  Insert = 'Insert',
  Update = 'Update',
  Delete = 'Delete',
}

export interface OverrideSettingExport {
  fieldName: string;
  value?: string | null;
}

export interface WaterfallRuleExport {
  expression?: string | null;
  ruleType: WaterfallRuleType;
  overrideSetting?: OverrideSettingExport | null;
}

export interface RefreshOrderSettingsExport {
  onCancelWaterfallName?: string | null;
  onDisconnectWaterfallName?: string | null;
  timeoutInMinutes?: number | null;
}

export interface StepExport {
  description: string;
  stepGuid: string;
  nextSteps: StepExport[];
  connectionNames: string[];
  selectionStrategy?: ProviderSelectionStrategy | null;
  waterfallRules: WaterfallRuleExport[];
  stepTimeoutInMinutes?: number | null;
  onTimeoutExceededAction: WaterfallStepAction;
  onFatalErrorAction?: WaterfallStepAction | null;
  onDisconnectWaterfallName?: string | null;
  onCancelWaterfallName?: string | null;
  multipleStepExecutionMode: MultipleStepExecutionMode;
}

export interface WaterfallVersionExport {
  versionNumber: number;
  executionMode: WaterfallExecutionMode;
  initialSteps: StepExport[];
  initialStepsExecutionMode: MultipleStepExecutionMode;
  refreshOrderSettings?: RefreshOrderSettingsExport | null;
  alwaysSendParentWaterfallDetailInWebhookEvents: boolean;
  notes?: string | null;
}

export interface WaterfallExport {
  name: string;
  description: string;
  integrationType: IntegrationType;
  versions: WaterfallVersionExport[];
}

export interface WaterfallMigrationExport {
  waterfalls: WaterfallExport[];
}

export interface WaterfallMigrationVersionDiff {
  action: WaterfallDiffAction;
  sourceVersionNumber: number;
  imported?: WaterfallVersionExport | null;
  current?: WaterfallVersionExport | null;
  changedFields: string[];
}

export interface WaterfallMigrationWaterfallDiff {
  action: WaterfallDiffAction;
  waterfallName: string;
  integrationType: IntegrationType;
  imported?: WaterfallExport | null;
  current?: WaterfallExport | null;
  versionDiffs: WaterfallMigrationVersionDiff[];
}

export interface WaterfallMigrationDiff {
  waterfallDiffs: WaterfallMigrationWaterfallDiff[];
  warnings: string[];
}

export interface WaterfallMigrationImportRequest {
  waterfallDiffs: WaterfallMigrationWaterfallDiff[];
  importNotes?: string | null;
}

export interface WaterfallMigrationImportResult {
  success: boolean;
  waterfallsInserted: number;
  waterfallsUpdated: number;
  versionsInserted: number;
  warnings: string[];
  errors: string[];
}

/** @deprecated Legacy DTO - use WaterfallViewModel instead */
export interface WaterfallDto {
  id?: string;
  name?: string;
  type?: string;
  lastExecuted?: Date | string;
  active?: boolean;
  updatedDate?: Date | string;
  updatedBy?: string;
}
